import { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronRight, Search, Table as TableIcon, AlertTriangle, Edit2, Trash2, FileSpreadsheet, CheckSquare, Square, UserCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, api } from "../../lib/api";
import { compactMoney, money, testid, getTodayDateString } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone, canDeleteLead } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";
import BulkLeadUploadModal from "./BulkLeadUploadModal";

const STATUSES = [
  "New",
  "Not Answered",
  "Not Interested",
  "Contacted",
  "Interested",
  "Qualified",
  "Proposal Sent",
  "Negotiation",
  "Won",
  "Lost",
];
const SOURCES = ["Website", "Referral", "Cold Call", "LinkedIn", "Event", "Email Inbound", "Other"];
const STATUS_TONE = {
  "New": "blue",
  "Not Answered": "amber",
  "Not Interested": "red",
  "Contacted": "blue",
  "Interested": "green",
  "Qualified": "amber",
  "Proposal Sent": "amber",
  "Negotiation": "amber",
  "Won": "green",
  "Lost": "red",
};

export default function Leads({ user, onOpenOrder, selectedLeadId, onOpenLead }) {
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [q, setQ] = useSavedState("leads", "q", "");
  const [statusFilter, setStatusFilter] = useSavedState("leads", "statusFilter", "");
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const headerCheckboxRef = useRef(null);

  const canEdit = isAdmin(user) || canAccessDepartment(user, ["Sales"]);
  const canDelete = canDeleteLead(user);
  const canAssign = canEdit;
  const canSelectLeads = canDelete || canAssign;

  const handleSelectLead = (id) => {
    if (onOpenLead) {
      onOpenLead(id);
    }
  };

  const load = async () => {
    const [l, p] = await Promise.all([api("get", "/leads"), api("get", "/leads/pipeline")]);
    setLeads(l.data); setPipeline(p.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (leads || []).filter((l) => {
    // Exclude converted leads from active leads list
    if (l.converted_client_id || l.is_converted) return false;

    // Enforce client-side safety guard in case of stale cache or fast transitions
    if (!isAdmin(user)) {
      const uName = (user?.name || "").trim().toLowerCase();
      const uId = (user?.user_id || "").trim().toLowerCase();
      const uEmail = (user?.email || "").trim().toLowerCase();
      const lAssigned = (l.assigned_to || "").trim().toLowerCase();
      const lRep = (l.sales_rep || "").trim().toLowerCase();
      const isUnassigned = (!lAssigned || lAssigned === "unassigned") && (!lRep || lRep === "unassigned");
      const isMine = (lAssigned && [uName, uId, uEmail].includes(lAssigned)) || (lRep && [uName, uId, uEmail].includes(lRep));
      if (!isUnassigned && !isMine) return false;
    }

    const lStatus = l.status || l.stage || "New";
    let okS = true;
    if (statusFilter === "Open") {
      okS = !["Won", "Lost"].includes(lStatus);
    } else if (statusFilter) {
      okS = lStatus === statusFilter;
    }
    const qTrim = q.trim().toLowerCase();
    const phoneVal = canViewClientPhone(user) ? (l.phone || l.mobile || l.contact_no || "") : "";
    const okQ = !qTrim || `${l.company_name || ""} ${l.contact_person || ""} ${l.contact_no || ""} ${l.job_title || ""} ${l.email || ""} ${l.work_email || ""} ${phoneVal} ${l.product_interest || l.requirement || ""} ${l.lead_id || ""} ${l.assigned_to || l.sales_rep || ""}`.toLowerCase().includes(qTrim);
    return okS && okQ;
  }), [leads, q, statusFilter, user]);

  const cardMetrics = useMemo(() => {
    let openVal = 0;
    let qualVal = 0;
    let wonVal = 0;
    let wonCount = 0;
    let lostCount = 0;
    const currentYear = new Date().getFullYear().toString();

    for (const l of (leads || [])) {
      if (l.converted_client_id || l.is_converted) continue;
      const st = l.status || l.stage || "New";
      const val = Number(l.estimated_value || l.value || l.amount || l.deal_size || l.total_value || l.order_value || 0);

      if (!["Won", "Lost"].includes(st)) {
        openVal += val;
      }
      if (st === "Qualified") {
        qualVal += val;
      }
      if (st === "Won") {
        const wonDate = (l.updated_at || l.created_at || "").slice(0, 4);
        if (!wonDate || wonDate === currentYear) {
          wonVal += val;
        }
        wonCount += 1;
      }
      if (st === "Lost") {
        lostCount += 1;
      }
    }

    const closed = wonCount + lostCount;
    const fallbackWinRate = closed > 0 ? Math.round((wonCount / closed) * 100) : 0;

    const open_value = pipeline?.open_value !== undefined ? pipeline.open_value : openVal;
    const qualified_value = pipeline?.totals?.["Qualified"] !== undefined ? pipeline.totals["Qualified"] : qualVal;
    const won_this_year = pipeline?.won_this_year !== undefined
      ? pipeline.won_this_year
      : ((pipeline?.totals?.["Won"] !== undefined) ? pipeline.totals["Won"] : wonVal);
    const win_rate = pipeline?.win_rate !== undefined ? pipeline.win_rate : fallbackWinRate;

    return {
      open_value,
      qualified_value,
      won_this_year,
      win_rate,
    };
  }, [leads, pipeline]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    paginatedItems: paginatedLeads,
    totalItems: totalFilteredLeads,
  } = usePagination(filtered, 10, { sectionKey: "leads" });

  const visibleLeadIds = useMemo(() => paginatedLeads.map((l) => l.lead_id), [paginatedLeads]);
  const isAllPageSelected = visibleLeadIds.length > 0 && visibleLeadIds.every((id) => selectedLeadIds.has(id));
  const isSomePageSelected = visibleLeadIds.some((id) => selectedLeadIds.has(id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = !isAllPageSelected && isSomePageSelected;
    }
  }, [isAllPageSelected, isSomePageSelected]);

  const handleToggleSelectAllPage = () => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (isAllPageSelected) {
        visibleLeadIds.forEach((id) => next.delete(id));
      } else {
        visibleLeadIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleToggleSelectLead = (leadId, e) => {
    e.stopPropagation();
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  return (
    <div className="content">
      <div className="page-heading">
        <div>
          <div className="small-label">SALES</div>
          <h1>Leads</h1>
          <p className="muted">Track prospects from first touch to Won. Convert them into clients in one click.</p>
        </div>
        <div className="stack" style={{ flexDirection: "row", gap: 10 }}>
          <div className="view-toggle" role="tablist">
            <button className="active" data-testid="leads-view-table" style={{ cursor: "default" }}>
              <TableIcon size={14} /> Table
            </button>
          </div>
          {canEdit && (
            <button
              className="outline-btn"
              data-testid="leads-import-button"
              onClick={() => setShowImport(true)}
            >
              <FileSpreadsheet size={14} /> Bulk Upload
            </button>
          )}
          {canEdit && (
            <button
              className="primary-btn compact"
              data-testid="leads-new-button"
              onClick={() => setShowNew(true)}
            >
              + New lead
            </button>
          )}
        </div>
      </div>

      {/* Summary / Status Cards (Interactive Filters) */}
      <div className="metrics-grid">
        <MetricBox
          label="Open pipeline"
          value={compactMoney(cardMetrics.open_value)}
          tone="blue"
          active={statusFilter === "Open"}
          onClick={() => {
            setStatusFilter(statusFilter === "Open" ? "" : "Open");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Qualified value"
          value={compactMoney(cardMetrics.qualified_value)}
          tone="amber"
          active={statusFilter === "Qualified"}
          onClick={() => {
            setStatusFilter(statusFilter === "Qualified" ? "" : "Qualified");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Won this year"
          value={compactMoney(cardMetrics.won_this_year)}
          tone="green"
          active={statusFilter === "Won"}
          onClick={() => {
            setStatusFilter(statusFilter === "Won" ? "" : "Won");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Win rate"
          value={`${cardMetrics.win_rate}%`}
          tone="green"
          active={statusFilter === "Won"}
          onClick={() => {
            setStatusFilter(statusFilter === "Won" ? "" : "Won");
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            placeholder={canViewClientPhone(user) ? "Search company, contact, email, phone, product…" : "Search company, contact, email, product…"}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCurrentPage(1);
            }}
            data-testid="leads-search-input"
          />
          {q && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setQ("");
                setCurrentPage(1);
              }}
              data-testid="leads-search-clear"
              style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          data-testid="leads-status-filter"
        >
          <option value="">All statuses</option>
          <option value="Open">Open pipeline (Active)</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(q || statusFilter) && (
          <button
            type="button"
            className="outline-btn compact"
            onClick={() => {
              setQ("");
              setStatusFilter("");
              setCurrentPage(1);
            }}
            style={{ fontSize: 12 }}
            title="Reset all filters"
            data-testid="leads-reset-filters-btn"
          >
            Reset Filters
          </button>
        )}
      </div>

      <section className="panel">
        <div className="panel-title">
          <div>
            <h3>All leads <span className="count">{filtered.length}</span></h3>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {canSelectLeads && selectedLeadIds.size > 0 && (
          <div
            className="bulk-actions-bar"
            data-testid="leads-bulk-actions-bar"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              padding: "10px 16px",
              marginBottom: 14,
              animation: "fadeIn 0.2s ease",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#1e40af" }} data-testid="leads-selected-count">
                {selectedLeadIds.size} lead{selectedLeadIds.size === 1 ? "" : "s"} selected
              </span>
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setSelectedLeadIds(new Set())}
                style={{ fontSize: 12, padding: "3px 10px", height: "auto", background: "#ffffff" }}
                data-testid="leads-clear-selection-btn"
              >
                Clear selection
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {canAssign && (
                <button
                  type="button"
                  className="primary-btn compact"
                  onClick={() => setShowBulkAssignModal(true)}
                  data-testid="leads-bulk-assign-btn"
                  style={{
                    background: "#2563eb",
                    borderColor: "#2563eb",
                    color: "#ffffff",
                    fontSize: 13,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    boxShadow: "0 1px 2px rgba(37, 99, 235, 0.2)",
                    cursor: "pointer",
                  }}
                >
                  <UserCheck size={14} /> Bulk Assign ({selectedLeadIds.size})
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="primary-btn compact"
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  data-testid="leads-bulk-delete-btn"
                  style={{
                    background: "#dc2626",
                    borderColor: "#dc2626",
                    color: "#ffffff",
                    fontSize: 13,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    boxShadow: "0 1px 2px rgba(220, 38, 38, 0.2)",
                    cursor: "pointer",
                  }}
                >
                  <Trash2 size={14} /> Bulk Delete ({selectedLeadIds.size})
                </button>
              )}
            </div>
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {canSelectLeads && (
                  <th style={{ width: 40, textAlign: "center", padding: "8px 6px" }}>
                    <input
                      type="checkbox"
                      ref={headerCheckboxRef}
                      checked={isAllPageSelected}
                      onChange={handleToggleSelectAllPage}
                      data-testid="leads-select-all-checkbox"
                      title={isAllPageSelected ? "Deselect all on this page" : "Select all on this page"}
                      style={{ cursor: "pointer", width: 16, height: 16 }}
                    />
                  </th>
                )}
                <th>Company</th>
                <th>Contact</th>
                <th>Contact Number</th>
                <th>Product Interest</th>
                <th>Source</th>
                <th>Owner</th>
                <th>Status</th>
                {canEdit && <th style={{ textAlign: "right", width: 90 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.map((l) => {
                const isSelected = selectedLeadIds.has(l.lead_id);
                return (
                  <tr
                    key={l.lead_id}
                    data-testid={`lead-row-${l.lead_id}`}
                    style={{
                      backgroundColor: isSelected ? "#f0f7ff" : undefined,
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    {canSelectLeads && (
                      <td
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 40, textAlign: "center", padding: "8px 6px" }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleToggleSelectLead(l.lead_id, e)}
                          data-testid={`lead-select-checkbox-${l.lead_id}`}
                          style={{ cursor: "pointer", width: 16, height: 16 }}
                        />
                      </td>
                    )}
                    <td onClick={() => handleSelectLead(l.lead_id)} style={{ cursor: "pointer" }}>
                      <b>{l.company_name}</b>
                      <small className="mono">#{l.lead_id}</small>
                    </td>
                    <td onClick={() => handleSelectLead(l.lead_id)} style={{ cursor: "pointer" }}>
                      <div>{l.contact_person || l.job_title || "—"}</div>
                      {(l.work_email || l.email) && <small className="muted">{l.work_email || l.email}</small>}
                    </td>
                    <td onClick={() => handleSelectLead(l.lead_id)} style={{ cursor: "pointer", fontFamily: "monospace" }}>
                      {canViewClientPhone(user) ? (l.contact_no || l.mobile || l.phone || "—") : "Protected"}
                    </td>
                    <td onClick={() => handleSelectLead(l.lead_id)} style={{ cursor: "pointer" }}>
                      {l.product_interest || l.requirement || "—"}
                    </td>
                    <td onClick={() => handleSelectLead(l.lead_id)} style={{ cursor: "pointer" }}>
                      {l.source || "—"}
                    </td>
                    <td
                      onClick={() => handleSelectLead(l.lead_id)}
                      style={{ cursor: "pointer" }}
                      data-testid={`lead-owner-${l.lead_id}`}
                    >
                      {l.assigned_to || l.sales_rep || "—"}
                    </td>
                    <td onClick={() => handleSelectLead(l.lead_id)} style={{ cursor: "pointer" }}>
                      <span className={`badge ${STATUS_TONE[l.status || l.stage || "New"] || "blue"}`}>
                        {l.status || l.stage || "New"}
                      </span>
                    </td>
                    {canEdit && (
                      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button
                            className="icon-btn"
                            onClick={() => setEditId(l.lead_id)}
                            data-testid={`lead-edit-${l.lead_id}`}
                            title="Edit lead"
                          >
                            <Edit2 size={14} />
                          </button>
                          {canDelete && (
                            <button
                              className="icon-btn"
                              onClick={() => setDeletingLead(l)}
                              data-testid={`lead-delete-${l.lead_id}`}
                              title="Delete lead"
                              style={{ color: "var(--red)" }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="empty-inline">No leads match this view.</div>}
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={totalFilteredLeads}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          itemLabel="leads"
        />
      </section>

      {showNew && (
        <NewLeadModal
          user={user}
          onClose={(refreshed) => {
            setShowNew(false);
            if (refreshed) {
              setStatusFilter("");
              setQ("");
              setCurrentPage(1);
              load();
            }
          }}
        />
      )}
      {showImport && (
        <BulkLeadUploadModal
          isOpen={showImport}
          onClose={(refreshed) => {
            setShowImport(false);
            if (refreshed) {
              setStatusFilter("");
              setQ("");
              setCurrentPage(1);
              load();
            }
          }}
          onCreated={() => {
            setStatusFilter("");
            setQ("");
            setCurrentPage(1);
            load();
          }}
        />
      )}
      {editId && <EditLeadModal leadId={editId} onClose={(refreshed) => { setEditId(null); if (refreshed) load(); }} />}
      {deletingLead && (
        <DeleteLeadConfirmationModal
          lead={deletingLead}
          onClose={() => setDeletingLead(null)}
          onDeleted={() => {
            setSelectedLeadIds((prev) => {
              const next = new Set(prev);
              next.delete(deletingLead.lead_id);
              return next;
            });
            load();
          }}
        />
      )}
      {showBulkDeleteConfirm && (
        <BulkDeleteLeadsConfirmationModal
          count={selectedLeadIds.size}
          leadIds={Array.from(selectedLeadIds)}
          onClose={() => setShowBulkDeleteConfirm(false)}
          onDeleted={() => {
            setSelectedLeadIds(new Set());
            load();
          }}
        />
      )}
      {showBulkAssignModal && (
        <BulkAssignLeadsModal
          leadIds={Array.from(selectedLeadIds)}
          leads={leads}
          onClose={() => setShowBulkAssignModal(false)}
          onAssigned={(assignedCount, assigneeName, assignedIds) => {
            const assignedIdSet = new Set(assignedIds || Array.from(selectedLeadIds));
            setLeads((prev) =>
              prev.flatMap((l) => {
                if (!assignedIdSet.has(l.lead_id)) return [l];
                if (!isAdmin(user) && assigneeName !== user?.name) return [];
                return [{ ...l, assigned_to: assigneeName, sales_rep: assigneeName }];
              })
            );
            setSelectedLeadIds(new Set());
            setShowBulkAssignModal(false);
            toast.success(`Successfully assigned ${assignedCount} lead${assignedCount === 1 ? "" : "s"} to ${assigneeName}`);
            load();
          }}
        />
      )}
    </div>
  );
}

function MetricBox({ label, value, tone = "blue", onClick, active }) {
  return (
    <div
      className={`metric ${tone}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        ...(active ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
      }}
      title={onClick ? `Filter by ${label}` : undefined}
      data-testid={`lead-metric-${testid(label)}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        {active && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
      </div>
      <strong>{value}</strong>
    </div>
  );
}

function DeleteLeadConfirmationModal({ lead, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api("delete", `/leads/${lead.lead_id}`);
      toast.success(`${lead.company_name} deleted successfully`);
      onDeleted();
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Could not delete lead";
      toast.error(errorMsg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10001 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        data-testid="delete-lead-confirmation-modal"
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: "0 0 6px 0", fontSize: 18, color: "var(--navy)" }}>
              Delete Lead
            </h2>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5 }}>
              Are you sure you want to delete this lead?
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}>
              <b style={{ color: "var(--navy)" }}>{lead.company_name}</b> will be removed from your active leads list.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            className="outline-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="cancel-delete-lead-button"
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleDelete}
            disabled={busy}
            style={{ background: "var(--red)", borderColor: "var(--red)" }}
            data-testid="confirm-delete-lead-button"
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkDeleteLeadsConfirmationModal({ count, leadIds, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);

  const handleBulkDelete = async () => {
    setBusy(true);
    try {
      const res = await api("post", "/leads/bulk-delete", { lead_ids: leadIds });
      toast.success(res.data?.message || `${count} lead(s) deleted successfully`);
      onDeleted();
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Could not delete selected leads";
      toast.error(errorMsg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10001 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        data-testid="bulk-delete-lead-confirmation-modal"
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: "0 0 6px 0", fontSize: 18, color: "var(--navy)" }}>
              Bulk Delete Leads
            </h2>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5 }}>
              Are you sure you want to delete {count} selected lead{count === 1 ? "" : "s"}?
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}>
              Selected leads will be removed from your active leads list.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            className="outline-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="cancel-bulk-delete-lead-button"
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleBulkDelete}
            disabled={busy}
            style={{ background: "var(--red)", borderColor: "var(--red)" }}
            data-testid="confirm-bulk-delete-lead-button"
          >
            {busy ? "Deleting…" : `Delete ${count} Lead${count === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkAssignLeadsModal({ leadIds, leads, onClose, onAssigned }) {
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const fetchSalesUsers = async () => {
      try {
        setLoadingUsers(true);
        const res = await api("get", "/users?department=Sales&status=active");
        let list = res.data || [];
        if (list.length === 0) {
          const fallbackRes = await api("get", "/users?role=sales&status=active");
          list = fallbackRes.data || [];
        }
        if (mounted) {
          setSalesUsers(list);
        }
      } catch (err) {
        console.error("Could not load sales users", err);
        if (mounted) {
          setError("Failed to load active Sales users");
        }
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    };
    fetchSalesUsers();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedLeadsDetails = useMemo(() => {
    const idSet = new Set(leadIds);
    return (leads || []).filter((l) => idSet.has(l.lead_id));
  }, [leads, leadIds]);

  const handleAssign = async () => {
    if (!selectedUser) {
      setError("Please select an active Sales user");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api("post", "/leads/bulk-assign", {
        lead_ids: leadIds,
        assigned_to: selectedUser,
      });
      const data = res.data || {};
      const count = data.assigned_count || leadIds.length;
      const targetName = data.assigned_to || selectedUser;
      const updatedIds = data.assigned_lead_ids || leadIds;
      onAssigned(count, targetName, updatedIds);
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not bulk assign leads";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10001 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 540 }}
        data-testid="bulk-assign-lead-modal"
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "#dbeafe",
              color: "#1d4ed8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UserCheck size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: "0 0 6px 0", fontSize: 18, color: "var(--navy)" }}>
              Bulk Assign Leads
            </h2>
            <p
              style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5 }}
              data-testid="bulk-assign-selected-count"
            >
              Assign {leadIds.length} selected lead{leadIds.length === 1 ? "" : "s"} to a Sales team member
            </p>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--muted)", lineHeight: 1.4 }}>
              All selected leads, including any currently assigned to other users, will be reassigned to the selected Sales user.
            </p>
          </div>
        </div>

        <div style={{ margin: "14px 0" }}>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--navy)",
              marginBottom: 6,
            }}
          >
            Select Sales User <span style={{ color: "var(--red)" }}>*</span>
          </label>
          <select
            value={selectedUser}
            onChange={(e) => {
              setSelectedUser(e.target.value);
              if (error) setError("");
            }}
            disabled={loadingUsers || busy}
            data-testid="bulk-assign-user-select"
            style={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              fontSize: 14,
              backgroundColor: "#ffffff",
              color: "#1e293b",
            }}
          >
            <option value="">
              {loadingUsers ? "Loading active Sales users..." : "-- Choose an active Sales user --"}
            </option>
            {salesUsers.map((u) => (
              <option key={u.user_id || u.name} value={u.name}>
                {u.name} {u.department || (u.departments && u.departments[0]) ? `(${u.departments ? u.departments.join(", ") : u.department})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Selected Leads Preview with Current Owners */}
        {selectedLeadsDetails.length > 0 && (
          <div style={{ margin: "14px 0" }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--muted)", marginBottom: 6 }}>
              Selected Leads & Current Owners ({selectedLeadsDetails.length})
            </div>
            <div
              style={{
                maxHeight: 160,
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                background: "#f8fafc",
                padding: "6px 10px",
                fontSize: 12.5,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #cbd5e1", textAlign: "left", color: "#64748b", fontSize: 11.5 }}>
                    <th style={{ padding: "4px 6px" }}>Company / Lead</th>
                    <th style={{ padding: "4px 6px" }}>Current Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLeadsDetails.map((l) => (
                    <tr key={l.lead_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "4px 6px" }}>
                        <span style={{ fontWeight: 600, color: "#1e293b" }}>{l.company_name}</span>{" "}
                        <span style={{ color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>#{l.lead_id}</span>
                      </td>
                      <td style={{ padding: "4px 6px", color: "#475569" }}>
                        {l.assigned_to || l.sales_rep || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Unassigned</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && (
          <div
            className="error"
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              background: "#fee2e2",
              color: "#b91c1c",
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button
            type="button"
            className="outline-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="cancel-bulk-assign-lead-button"
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleAssign}
            disabled={busy || !selectedUser || loadingUsers}
            style={{
              background: "var(--navy)",
              borderColor: "var(--navy)",
              color: "#ffffff",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            data-testid="confirm-bulk-assign-lead-button"
          >
            <UserCheck size={14} />
            {busy ? "Assigning…" : `Assign ${leadIds.length} Lead${leadIds.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewLeadModal({ user, onClose }) {
  const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", phone: "", source: "Website", estimated_value: 0, product_interest: "", assigned_to: "", notes: "", company: "" });
  const [salesUsers, setSalesUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  
  useEffect(() => {
    // Fetch sales users and active companies for dropdowns
    const loadData = async () => {
      try {
        const [salesRes, companiesRes] = await Promise.all([
          api("get", "/users?role=sales"),
          api("get", "/companies"),
        ]);
        setSalesUsers(salesRes.data || []);
        // Filter only active companies
        const activeCompanies = (companiesRes.data || []).filter((c) => c.is_active);
        setCompanies(activeCompanies);
        // Set first active company as default if available
        if (activeCompanies.length > 0) {
          setForm((f) => ({ ...f, company: activeCompanies[0].name }));
        }
      } catch (err) {
        console.error("Could not load data:", err);
      }
    };
    loadData();
  }, []);
  
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await api("post", "/leads", { ...form, estimated_value: Number(form.estimated_value || 0) });
      toast.success("Lead created");
      onClose(true);
    } catch (err) { setError(err.response?.data?.detail || "Please complete lead details"); } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-lead-form">
        <div className="modal-head"><div><div className="small-label">SALES</div><h2>New lead</h2></div><button type="button" className="icon-btn" onClick={() => onClose(false)}><X size={18} /></button></div>
        <div className="form-grid">
          <label>
            Company / Organization <span style={{ color: "var(--red)" }}>*</span>
            <select required value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="lead-company-select">
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>Company<input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="lead-company-input" /></label>
          <label>Contact person<input required value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="lead-contact-input" /></label>
          <label>Email<input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email-input" /></label>
          <label>Phone<input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone-input" /></label>
          <label>Source<select value={form.source} onChange={(e) => set("source", e.target.value)} data-testid="lead-source-select">{SOURCES.map((s) => <option key={s}>{s}</option>)}</select></label>
          <label>Estimated value (₹)<input type="number" min="0" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} data-testid="lead-value-input" /></label>
          <label>Product interest<input value={form.product_interest} onChange={(e) => set("product_interest", e.target.value)} data-testid="lead-product-input" /></label>
          <label>
            Assign to
            <select value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} data-testid="lead-assign-to-select">
              <option value="">Unassigned</option>
              <option value="auto">Auto-assign (Random Sales User)</option>
              {salesUsers.map((u) => (
                <option key={u.user_id} value={u.name}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="full">Notes<textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="lead-notes-input" /></label>
        {error && <div className="error">{error}</div>}
        <button className="primary-btn wide" data-testid="lead-submit-button" disabled={busy}>{busy ? "Saving…" : "Create lead"} <ChevronRight size={16} /></button>
      </form>
    </div>
  );
}

export function EditLeadModal({ leadId, onClose }) {
  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(null);
  const [salesUsers, setSalesUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [res, usersRes, companiesRes] = await Promise.all([
          api("get", `/leads/${leadId}`),
          api("get", "/users?role=sales&status=active"),
          api("get", "/companies"),
        ]);
        setLead(res.data);
        setSalesUsers(usersRes.data || []);
        // Filter only active companies
        const activeCompanies = (companiesRes.data || []).filter((c) => c.is_active);
        setCompanies(activeCompanies);
        setForm({
          company_name: res.data.company_name || "",
          contact_person: res.data.contact_person || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          source: res.data.source || "Website",
          estimated_value: res.data.estimated_value || 0,
          product_interest: res.data.product_interest || "",
          assigned_to: res.data.assigned_to || res.data.sales_rep || "",
          next_follow_up: res.data.next_follow_up || "",
          follow_up_notes: res.data.follow_up_notes || "",
          status: res.data.status || "New",
          notes: res.data.notes || "",
          company: res.data.company || "",
        });
      } catch (err) {
        toast.error("Could not load lead details");
        onClose(false);
      }
    };
    load();
  }, [leadId, onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("patch", `/leads/${leadId}`, {
        ...form,
        estimated_value: Number(form.estimated_value || 0),
      });
      toast.success("Lead updated");
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not update lead");
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return (
      <div className="modal-backdrop">
        <div className="modal">Loading…</div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-lead-form">
        <div className="modal-head">
          <div>
            <div className="small-label">SALES · {lead?.lead_id}</div>
            <h2>Edit lead</h2>
          </div>
          <button type="button" className="icon-btn" onClick={() => onClose(false)}>
            <X size={18} />
          </button>
        </div>
        <div className="form-grid">
          <label>
            Company / Organization
            <select
              required
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              data-testid="edit-lead-company-select"
            >
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Company
            <input
              required
              value={form.company_name}
              onChange={(e) => set("company_name", e.target.value)}
              data-testid="edit-lead-company-input"
            />
          </label>
          <label>
            Contact person
            <input
              required
              value={form.contact_person}
              onChange={(e) => set("contact_person", e.target.value)}
              data-testid="edit-lead-contact-input"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              data-testid="edit-lead-email-input"
            />
          </label>
          <label>
            Phone
            <input
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              data-testid="edit-lead-phone-input"
            />
          </label>
          <label>
            Source
            <select
              value={form.source}
              onChange={(e) => set("source", e.target.value)}
              data-testid="edit-lead-source-select"
            >
              {SOURCES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label>
            Estimated value (₹)
            <input
              type="number"
              min="0"
              value={form.estimated_value}
              onChange={(e) => set("estimated_value", e.target.value)}
              data-testid="edit-lead-value-input"
            />
          </label>
          <label>
            Product interest
            <input
              value={form.product_interest}
              onChange={(e) => set("product_interest", e.target.value)}
              data-testid="edit-lead-product-input"
            />
          </label>
          <label>
            Assigned to
            <select
              value={form.assigned_to}
              onChange={(e) => set("assigned_to", e.target.value)}
              data-testid="edit-lead-assigned-select"
            >
              <option value="">Unassigned</option>
              {form.assigned_to && !salesUsers.some((u) => u.name === form.assigned_to) && (
                <option value={form.assigned_to}>{form.assigned_to}</option>
              )}
              {salesUsers.map((u) => (
                <option key={u.user_id || u.name} value={u.name}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Next follow-up
            <input
              type="date"
              value={form.next_follow_up}
              min={getTodayDateString()}
              onChange={(e) => set("next_follow_up", e.target.value)}
              data-testid="edit-lead-followup-input"
            />
          </label>
          <label>
            Follow-up reminder / agenda
            <input
              type="text"
              placeholder="Brief reminder / agenda..."
              value={form.follow_up_notes || ""}
              onChange={(e) => set("follow_up_notes", e.target.value)}
              data-testid="edit-lead-followup-notes-input"
            />
          </label>
          <label>
            Status
            <select
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              data-testid="edit-lead-status-select"
            >
              {STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="full">
          Notes
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            data-testid="edit-lead-notes-input"
          />
        </label>
        {error && <div className="error">{error}</div>}
        <button className="primary-btn wide" data-testid="edit-lead-submit-button" disabled={busy}>
          {busy ? "Saving…" : "Update lead"} <ChevronRight size={16} />
        </button>
      </form>
    </div>
  );
}
